import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { StorageService } from 'src/app/common/services/storage/storage.service';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import {
  getStoredFormData,
  hasStoredFormData,
} from 'src/app/common/utilities/proform-helpers';
import { ProformViewStates } from '../../../dtos/proforms.dto';
import { AlertService, AlertType } from '../../../services/alert/alert.service';
import { ProformsSubmissionService } from '../../../services/proforms/proforms-submission.service';
import { CreateProformSubmissionPayload } from '../../../types/payloads/proforms.payloads';
import {
  ProFormAnonymity,
  ProFormContext,
  ProFormsAPIResponse,
  ProFormsSubmissionAPIResponse,
  ProFormType,
} from '../../../types/responses/proforms.responses';

interface ValidationError {
  questionId: number;
  message: string;
}
@Component({
  selector: 'app-proforms-form',
  standalone: false,
  templateUrl: './proforms-form.component.html',
  styleUrl: './proforms-form.component.scss',
})
export class ProformsFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() viewState: ProformViewStates = ProformViewStates.PREVIEW;

  @Input() formId: number;

  @Input() formData?: ProFormsAPIResponse;

  @Input() context: ProFormContext;

  @Input() contextId: number;

  @Input() submissionId?: number;

  @Input() observeeUserId?: number;

  @Input() isObservationSession = false;

  @Input() hideTitle = false;

  @Output() readonly formSubmitted =
    new EventEmitter<ProFormsSubmissionAPIResponse>();

  @Output() readonly showGradedQuiz = new EventEmitter<{
    formId: number;
    submissionId: number;
  }>();

  user: User;

  isLoading = false;

  errorMessage: string;

  currentSectionIndex = 0;

  submitLoading = false;

  form: ProFormsAPIResponse;

  submission: ProFormsSubmissionAPIResponse | null;

  viewStates = ProformViewStates;

  ProFormAnonymity = ProFormAnonymity;

  hasRequiredFields = false;

  submitted = false;

  validationErrors: ValidationError[] = [];

  newSubmissionPayload: CreateProformSubmissionPayload | null = null;

  STORAGE_KEY: string;

  subs: Subscription[] = [];

  ProFormType = ProFormType;

  ProFormContext = ProFormContext;

  constructor(
    private store: Store,
    private proformsSubmissionService: ProformsSubmissionService,
    private alertService: AlertService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
  }

  private computeHasRequiredFields() {
    this.hasRequiredFields = false;
    this.form?.sections?.forEach((section) => {
      section.questions.forEach((question) => {
        if (question.required) {
          this.hasRequiredFields = true;
        }
      });
    });
  }

  ngOnInit() {
    if (this.formData) {
      this.form = this.formData;
      this.computeHasRequiredFields();
    } else if (this.viewState === this.viewStates.PREVIEW) {
      this.getForm();
    } else if (
      this.submissionId &&
      (this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED)
    ) {
      this.getSubmission();
      this.subs.push(
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            if (submission && submission.id === this.submissionId) {
              this.submission = submission;
              this.formId = this.submission.proform_form_id;
              this.getForm();
            }
          }
        )
      );
    } else if (this.viewState === this.viewStates.EDIT) {
      if (this.context && this.contextId) {
        this.STORAGE_KEY = `proform-${this.formId}-submission-payload-${
          this.context
        }-${this.contextId}${this.user ? `-user-${this.user.id}` : ''}${
          this.observeeUserId ? `-observee-${this.observeeUserId}` : ''
        }`;

        this.subs.push(
          this.proformsSubmissionService.newSubmission.subscribe(
            (submission) => {
              if (
                submission &&
                submission.context_table === this.context &&
                submission.context_table_id === this.contextId
              ) {
                this.newSubmissionPayload = submission;
                StorageService.storeItem(
                  this.STORAGE_KEY,
                  this.newSubmissionPayload
                );
              }
            }
          )
        );

        // If submissionId is provided in EDIT mode, pre-populate from existing submission
        if (this.submissionId) {
          this.getSubmissionForEditing();
        } else {
          // Create new submission payload
          this.proformsSubmissionService.createPayload(
            this.context,
            this.contextId,
            hasStoredFormData(this.STORAGE_KEY)
              ? getStoredFormData(this.STORAGE_KEY)
              : []
          );
        }
      }
      this.getForm();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formData'] && this.formData) {
      this.form = this.formData;
      this.computeHasRequiredFields();
    } else if (
      changes['submissionId'] &&
      !changes['submissionId'].firstChange
    ) {
      if (
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED
      ) {
        this.getSubmission();
      } else if (this.viewState === this.viewStates.EDIT && this.submissionId) {
        this.getSubmissionForEditing();
      }
    } else if (
      changes['formId'] &&
      !changes['formId'].firstChange &&
      this.viewState === this.viewStates.PREVIEW
    ) {
      this.getForm();
    }
  }

  getForm() {
    this.isLoading = true;
    this.proformsSubmissionService.getProForm(this.formId).subscribe({
      next: (response: { item: ProFormsAPIResponse }) => {
        this.isLoading = false;
        this.form = response.item;
        this.computeHasRequiredFields();
      },
      error: (error) => {
        if (error) {
          this.isLoading = false;
          this.errorMessage = error.error.message;
        }
      },
    });
  }

  getSubmission() {
    if (this.submissionId) {
      this.proformsSubmissionService.getSubmission(this.submissionId);
    }
  }

  getSubmissionForEditing() {
    if (this.submissionId) {
      this.proformsSubmissionService.getSubmission(this.submissionId);
      this.subs.push(
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            if (submission && submission.id === this.submissionId) {
              this.submission = submission;
              this.formId = this.submission.proform_form_id;

              // Convert existing submission answers to editable payload format
              const payloadAnswers = (submission.answers || []).map(
                (answer) => ({
                  proform_form_submission_id: answer.proform_form_submission_id,
                  proform_form_question_id: answer.proform_form_question_id,
                  proform_form_question_answer_option_id:
                    answer.proform_form_question_answer_option_id,
                  text_answer: answer.text_answer,
                  // Note: selectedCell is read-only and managed by the backend
                })
              );
              this.proformsSubmissionService.createPayload(
                this.context,
                this.contextId,
                payloadAnswers
              );
            }
          }
        )
      );
    }
  }

  submitForm() {
    if (this.viewState === this.viewStates.EDIT) {
      this.submitLoading = true;
      this.submitted = true;
      const formIsValid = this.validateForm();

      if (!formIsValid) {
        this.submitLoading = false;
      } else if (this.newSubmissionPayload) {
        // remove answerAttachment, not recognized by backend
        this.newSubmissionPayload.answers.forEach((answer) => {
          if (answer.answerAttachment) {
            delete answer.answerAttachment;
          }

          // For option-based questions (radio, dropdown, etc.),
          // ensure we do not send a literal 'null' text_answer
          // which becomes the string 'null' in FormData.
          if (
            answer.proform_form_question_answer_option_id != null &&
            (answer.text_answer === null ||
              answer.text_answer === undefined ||
              answer.text_answer === 'null')
          ) {
            delete answer.text_answer;
          }
        });

        // Check if we're updating an existing submission
        if (this.submissionId && this.submission) {
          this.proformsSubmissionService
            .editSubmission(this.submissionId, this.newSubmissionPayload)
            .subscribe({
              next: (res) => {
                if (res) {
                  this.submitLoading = false;
                  StorageService.removeItem(this.STORAGE_KEY);
                  this.nextSection();
                  this.formSubmitted.emit(res.item);
                  this.submission = res.item;
                  this.proformsSubmissionService.resetNewSubmission();
                }
              },
              error: (error) => {
                this.submitLoading = false;
                const message =
                  error?.error?.message || 'Failed to update submission.';
                this.errorMessage = message;
                this.alertService.showAlert(
                  message,
                  undefined,
                  AlertType.DANGER
                );
              },
            });
        } else {
          // Create a new submission
          this.proformsSubmissionService
            .createSubmission(this.formId, this.newSubmissionPayload)
            .subscribe({
              next: (res) => {
                if (res) {
                  this.submitLoading = false;
                  StorageService.removeItem(this.STORAGE_KEY);
                  this.nextSection();
                  this.formSubmitted.emit(res.item);
                  this.submission = res.item;
                  this.proformsSubmissionService.resetNewSubmission();
                }
              },
              error: (error) => {
                this.submitLoading = false;
                const message =
                  error?.error?.message || 'Failed to submit form.';
                this.errorMessage = message;
                this.alertService.showAlert(
                  message,
                  undefined,
                  AlertType.DANGER
                );
              },
            });
        }
      } else {
        this.submitLoading = false;
      }
    }
  }

  validateForm(): boolean {
    if (!this.form?.sections) return true;

    const currentSection = this.form.sections[this.currentSectionIndex];
    if (!currentSection) return true;

    const hasMissingRequiredAnswer = currentSection.questions
      .filter((q) => q.required)
      .some((question) => {
        const answer = this.newSubmissionPayload?.answers.find(
          (ans) => ans.proform_form_question_id === question.id
        );

        const missing = !answer;
        return missing;
      });

    return !hasMissingRequiredAnswer;
  }

  nextSection() {
    if (this.form.sections) {
      this.currentSectionIndex += 1;
    }
  }

  prevSection() {
    if (this.currentSectionIndex > 0) {
      this.currentSectionIndex -= 1;
    }
  }

  onGradedViewRequested() {
    if (this.submission?.id && this.formId) {
      this.showGradedQuiz.emit({
        formId: this.formId,
        submissionId: this.submission.id,
      });
    }
  }

  resetForm() {
    this.currentSectionIndex = 0;
    this.submitted = false;
  }

  ngOnDestroy() {
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }
  }
}
